import * as React from 'react';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { useAuth0 } from '@auth0/auth0-react';
import Container from '@mui/material/Container';
import {
  AuthProps,
  ChangePasswordValues,
  UpdatePasswordData,
} from '../../types/props';
import { Alert, CircularProgress, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import {
  getBarberById,
  getShopBarberById,
  getShopOwnerById,
} from '../../middleware/registration_service/getRequests';
import { UserRole } from '../../types/role';

const schema = object({
  email: string()
    .required('Email is required')
    .email('Please enter a valid email address'),
});

/** Fetches the partner record by stored ID and returns the registered email, or null on failure. */
const fetchRegisteredEmail = async (
  role: string | null
): Promise<string | null> => {
  const partnerItem = localStorage.getItem('reg_partner_id');
  if (!partnerItem) return null;

  try {
    const partnerId = BigInt(partnerItem);
    let res;
    switch (role) {
      case UserRole.SHOP_OWNER:
        res = await getShopOwnerById(partnerId);
        break;
      case UserRole.SHOP_BARBER:
        res = await getShopBarberById(partnerId);
        break;
      case UserRole.BARBER:
        res = await getBarberById(partnerId);
        break;
      default:
        return null;
    }
    return res?.data?.email ?? null;
  } catch {
    return null;
  }
};

export default function PasswordReset({ domain, clientId }: AuthProps) {
  const { user } = useAuth0();
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  );
  const [checking, setChecking] = React.useState<boolean>(false);

  const initialValues: ChangePasswordValues = {
    email: user?.email || '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values: ChangePasswordValues): Promise<void> => {
      setValidationError(null);
      setChecking(true);

      // ── Step 1: confirm the email matches the registered account ────────
      const role = localStorage.getItem('role');
      const registeredEmail = await fetchRegisteredEmail(role);

      if (registeredEmail === null) {
        setValidationError(
          'Unable to verify your email right now. Please try again later.'
        );
        setChecking(false);
        return;
      }

      if (
        registeredEmail.toLowerCase().trim() !==
        values.email.toLowerCase().trim()
      ) {
        setValidationError(
          'No account found for this email address. Please check and try again.'
        );
        setChecking(false);
        return;
      }

      // ── Step 2: send the Auth0 password reset email ──────────────────────
      const data: UpdatePasswordData = {
        clientId,
        connection: 'Username-Password-Authentication',
        email: values.email,
      };

      try {
        await fetch(`https://${domain}/dbconnections/change_password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        setSubmitted(true);
      } catch (error) {
        setValidationError(
          `Error sending password reset email to ${values.email}. Please try again.`
        );
      } finally {
        setChecking(false);
      }
    },
  });

  return (
    <Container sx={{ mt: '1em' }}>
      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          label="Email"
          value={formik.values.email}
          onChange={(e) => {
            formik.handleChange(e);
            setValidationError(null);
          }}
          error={
            (formik.touched.email && Boolean(formik.errors.email)) ||
            !!validationError
          }
          helperText={
            (formik.touched.email && formik.errors.email) || validationError
          }
          disabled={submitted || checking}
        />
        <Button
          disabled={submitted || checking}
          color="primary"
          variant="contained"
          fullWidth
          type="submit"
          sx={{ mt: '0.5em' }}
          startIcon={
            checking ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {checking ? 'Verifying...' : 'Reset Password'}
        </Button>
      </form>

      {submitted && (
        <Alert
          onClose={() => setSubmitted(false)}
          severity="success"
          sx={{ mt: '0.5em' }}
        >
          Password reset email sent to {formik.values.email}
        </Alert>
      )}
    </Container>
  );
}
